// ** React Imports
import React, { forwardRef } from "react";
import moment from 'moment';
import QRCode from 'react-qr-code';

// ** Reactstrap Imports
import { Container } from "reactstrap";

//redux
import { selectLanguageUse } from '../../../redux/slices/language.Slice'
import { useSelector } from "react-redux";

// Images
import LogoProgress from '../../../assets/images/LogoProgress.png'
import footerLogo from '../../../assets/images/FooterLogo.png'

// Utility-Service
import { getUser } from "../../../utility/AuthService";

const ESGCertificate = forwardRef((props, ref) => {
    const profileData = props?.profileData;
    const ESGperformanceData = props?.ESGperformanceData;
    const verificationUrl = props?.verificationUrl || '';
    const user = getUser();
    const languageUse = useSelector(selectLanguageUse || user?.language);

    const colorLeval = [
        {
            leval: "Laggard",
            color_code: "#FF0000"
        },
        {
            leval: "Beginner",
            color_code: "#FF9900"
        },
        {
            leval: "Intermediate",
            color_code: "#B6D7A8"
        },
        {
            leval: "Advanced",
            color_code: "#93C47D"
        },
        {
            leval: "Leader",
            color_code: "#38761D"
        },
    ];

    const currentMaturityLevel = ESGperformanceData?.leval || 'Beginner';
    const levelColor = colorLeval.find(level => level.leval === currentMaturityLevel)?.color_code || "#009edb";
    const maturityLevelText = languageUse === 'English' 
        ? currentMaturityLevel 
        : ESGperformanceData?.data?.leval_Malay || currentMaturityLevel;

    // Calculate overall score percentage
    // Try to get from props first, then from ESGperformanceData, or calculate from indicatorData
    let scorePercentage = '0.0';
    
    if (props?.overallScorePercent !== undefined && props?.overallScorePercent !== null) {
        scorePercentage = parseFloat(props.overallScorePercent).toFixed(1);
    } else if (ESGperformanceData?.scorePercent !== undefined && ESGperformanceData?.scorePercent !== null) {
        scorePercentage = parseFloat(ESGperformanceData.scorePercent).toFixed(1);
    } else if (ESGperformanceData?.overallScore && ESGperformanceData?.totalScore) {
        const overallScore = ESGperformanceData.overallScore;
        const totalScore = ESGperformanceData.totalScore;
        scorePercentage = totalScore > 0 ? ((overallScore / totalScore) * 100).toFixed(1) : '0.0';
    } else if (props?.indicatorData && Array.isArray(props.indicatorData) && props.indicatorData.length > 0) {
        // Calculate from indicator data
        const indicatorData = props.indicatorData;
        const correctSum = indicatorData.reduce((sum, ind) => sum + (Number(ind.correct) || 0), 0);
        const totalSum = indicatorData.reduce((sum, ind) => sum + (Number(ind.total) || 0), 0);
        if (totalSum > 0) {
            scorePercentage = ((correctSum / totalSum) * 100).toFixed(1);
        }
    }

    const certificateDate = moment().format('DD MMMM YYYY');
    const certificateYear = moment().format('YYYY');

    const certificateStyles = {
        certificateContainer: {
            width: '210mm',
            minHeight: '297mm',
            margin: '0 auto',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            border: '6px solid #2c3e50',
            borderRadius: '10px',
            padding: '25px 35px',
            position: 'relative',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            fontFamily: '"Georgia", "Times New Roman", serif',
        },
        decorativeBorder: {
            position: 'absolute',
            top: '15px',
            left: '15px',
            right: '15px',
            bottom: '15px',
            border: '2px solid #009edb',
            borderRadius: '6px',
            pointerEvents: 'none',
        },
        cornerDecoration: {
            position: 'absolute',
            width: '45px',
            height: '45px',
            border: '2px solid',
            borderColor: levelColor,
        },
        topLeftCorner: {
            top: '20px',
            left: '20px',
            borderRight: 'none',
            borderBottom: 'none',
        },
        topRightCorner: {
            top: '20px',
            right: '20px',
            borderLeft: 'none',
            borderBottom: 'none',
        },
        bottomLeftCorner: {
            bottom: '20px',
            left: '20px',
            borderRight: 'none',
            borderTop: 'none',
        },
        bottomRightCorner: {
            bottom: '20px',
            right: '20px',
            borderLeft: 'none',
            borderTop: 'none',
        },
        header: {
            textAlign: 'center',
            marginBottom: '20px',
            paddingTop: '10px',
        },
        logo: {
            maxWidth: '180px',
            marginBottom: '12px',
        },
        title: {
            fontSize: '32px',
            fontWeight: '700',
            color: '#2c3e50',
            letterSpacing: '2px',
            marginBottom: '8px',
            textTransform: 'uppercase',
            fontFamily: '"Georgia", "Times New Roman", serif',
        },
        subtitle: {
            fontSize: '14px',
            color: '#6c757d',
            fontWeight: '400',
            letterSpacing: '1px',
            marginBottom: '25px',
        },
        mainContent: {
            textAlign: 'center',
            marginTop: '25px',
            marginBottom: '25px',
        },
        certificateText: {
            fontSize: '18px',
            color: '#495057',
            lineHeight: '1.6',
            marginBottom: '18px',
            fontStyle: 'italic',
        },
        companyName: {
            fontSize: '36px',
            fontWeight: '700',
            color: '#2c3e50',
            marginTop: '15px',
            marginBottom: '20px',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            fontFamily: '"Georgia", "Times New Roman", serif',
        },
        achievementSection: {
            marginTop: '25px',
            marginBottom: '25px',
        },
        maturityBadge: {
            display: 'inline-block',
            padding: '12px 35px',
            background: `linear-gradient(135deg, ${levelColor} 0%, ${levelColor}dd 100%)`,
            color: '#ffffff',
            borderRadius: '40px',
            fontSize: '24px',
            fontWeight: '700',
            letterSpacing: '1.5px',
            boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
            marginTop: '15px',
            marginBottom: '15px',
            textTransform: 'uppercase',
        },
        scoreDisplay: {
            fontSize: '56px',
            fontWeight: '700',
            color: levelColor,
            marginTop: '15px',
            marginBottom: '8px',
            fontFamily: '"Georgia", "Times New Roman", serif',
        },
        scoreLabel: {
            fontSize: '14px',
            color: '#6c757d',
            fontWeight: '400',
            marginTop: '8px',
        },
        dateSection: {
            marginTop: '35px',
            fontSize: '13px',
            color: '#6c757d',
        },
        footer: {
            position: 'absolute',
            bottom: '20px',
            left: '35px',
            right: '35px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            borderTop: '2px solid #e9ecef',
            paddingTop: '15px',
        },
        signatureLine: {
            width: '180px',
            borderTop: '2px solid #2c3e50',
            marginTop: '40px',
            textAlign: 'center',
            paddingTop: '8px',
            fontSize: '11px',
            color: '#6c757d',
        },
        qrCodeContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px',
            background: '#ffffff',
            borderRadius: '8px',
            border: `2px solid ${levelColor}`,
        },
        qrCode: {
            width: '80px',
            height: '80px',
        },
        qrCodeLabel: {
            fontSize: '9px',
            color: '#6c757d',
            marginTop: '4px',
            textAlign: 'center',
            fontWeight: '500',
        },
        decorativeLine: {
            width: '180px',
            height: '2px',
            background: `linear-gradient(90deg, transparent, ${levelColor}, transparent)`,
            margin: '20px auto',
        },
    };

    return (
        <Container>
            <div ref={ref} className="px-4 w-100 pdfFont" style={{ display: 'block' }}>
                <div style={certificateStyles.certificateContainer}>
                    {/* Decorative Border */}
                    <div style={certificateStyles.decorativeBorder}></div>

                    {/* Corner Decorations */}
                    <div style={{ ...certificateStyles.cornerDecoration, ...certificateStyles.topLeftCorner }}></div>
                    <div style={{ ...certificateStyles.cornerDecoration, ...certificateStyles.topRightCorner }}></div>
                    <div style={{ ...certificateStyles.cornerDecoration, ...certificateStyles.bottomLeftCorner }}></div>
                    <div style={{ ...certificateStyles.cornerDecoration, ...certificateStyles.bottomRightCorner }}></div>

                    {/* Header */}
                    <div style={certificateStyles.header}>
                        <img src={LogoProgress} alt="Logo" style={certificateStyles.logo} />
                        <h1 style={certificateStyles.title}>
                            {languageUse === 'English' ? 'CERTIFICATE OF ACHIEVEMENT' : 'SIJIL PENCAPAIAN'}
                        </h1>
                        <p style={certificateStyles.subtitle}>
                            {languageUse === 'English' 
                                ? 'ESG Maturity Assessment' 
                                : 'Penilaian Kematangan ESG'}
                        </p>
                    </div>

                    {/* Decorative Line */}
                    <div style={certificateStyles.decorativeLine}></div>

                    {/* Main Content */}
                    <div style={certificateStyles.mainContent}>
                        <p style={certificateStyles.certificateText}>
                            {languageUse === 'English' 
                                ? 'This is to certify that' 
                                : 'Ini adalah untuk mengesahkan bahawa'}
                        </p>

                        <div style={certificateStyles.companyName}>
                            {profileData?.company || 'Company Name'}
                        </div>

                        <p style={certificateStyles.certificateText}>
                            {languageUse === 'English' 
                                ? 'has successfully completed the ESG Maturity Assessment and achieved' 
                                : 'telah berjaya menamatkan Penilaian Kematangan ESG dan mencapai'}
                        </p>

                        {/* Achievement Section */}
                        <div style={certificateStyles.achievementSection}>
                            <div style={certificateStyles.maturityBadge}>
                                {maturityLevelText}
                            </div>
                            <div style={certificateStyles.scoreDisplay}>
                                {scorePercentage}%
                            </div>
                            <p style={certificateStyles.scoreLabel}>
                                {languageUse === 'English' 
                                    ? 'Overall ESG Maturity Score' 
                                    : 'Skor Kematangan ESG Keseluruhan'}
                            </p>
                        </div>

                        <p style={certificateStyles.certificateText}>
                            {languageUse === 'English' 
                                ? `in the year ${certificateYear}` 
                                : `pada tahun ${certificateYear}`}
                        </p>
                    </div>

                    {/* Decorative Line */}
                    <div style={certificateStyles.decorativeLine}></div>

                    {/* Footer */}
                    <div style={certificateStyles.footer}>
                        <div>
                            <div style={certificateStyles.dateSection}>
                                <strong>{languageUse === 'English' ? 'Date' : 'Tarikh'}:</strong> {certificateDate}
                            </div>
                        </div>
                        <div style={certificateStyles.qrCodeContainer}>
                            {verificationUrl ? (
                                <>
                                    <div style={certificateStyles.qrCode}>
                                        <QRCode
                                            value={verificationUrl}
                                            size={80}
                                            style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                            viewBox={`0 0 80 80`}
                                        />
                                    </div>
                                    <div style={certificateStyles.qrCodeLabel}>
                                        {certificateYear}
                                    </div>
                                </>
                            ) : (
                                <div style={{ fontSize: '10px', color: '#6c757d' }}>
                                    {certificateYear}
                                </div>
                            )}
                        </div>
                        <div>
                            <div style={certificateStyles.signatureLine}>
                                <div style={{ fontSize: '11px', fontWeight: '600', color: '#2c3e50' }}>
                                    UN Global Compact South Africa
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer Logo */}
                    <div style={{ textAlign: 'center', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e9ecef' }}>
                        <img src={footerLogo} alt="Footer Logo" style={{ maxWidth: '120px', opacity: 0.7 }} />
                    </div>
                </div>
            </div>
        </Container>
    );
});

ESGCertificate.displayName = 'ESGCertificate';

export default ESGCertificate;

